import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { LabelService } from '../../services/label.service';
import { AddEventLifeLine } from '../../models/life-line-models';
import moment from 'moment';

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: ['DD/MM/YYYY', 'MM/YYYY'],
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-panel-lifeline-event',
  templateUrl: './panel-lifeline-event.component.html',
  styleUrls: ['./panel-lifeline-event.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
  ],
  standalone: false,
})
export class PanelLifelineEventComponent implements OnInit {
  @Input() panelTitle: string = '';
  @Input() eventTitle: string = '';
  @Input() eventStartDay: string = '';
  @Input() eventStartMonth: string = '';
  @Input() eventStartYear: string = '';
  @Input() eventEndDay: string = '';
  @Input() eventEndMonth: string = '';
  @Input() eventEndYear: string = '';
  @Output() cancel = new EventEmitter<void>();
  @Output() confirm = new EventEmitter<AddEventLifeLine>();

  eventForm: FormGroup;
  isBtnClicked: boolean = false;

  constructor(
    public labelService: LabelService,
    private fb: FormBuilder,
    private dateAdapter: DateAdapter<any>
  ) {
    this.eventForm = this.fb.group({
      title: ['', Validators.required],
      startDate: ['', Validators.required],
      endDate: [null],
      isMonthOnly: [true],
    });

    this.dateAdapter.setLocale('it-IT');

    this.eventForm
      .get('isMonthOnly')
      ?.valueChanges.subscribe((isMonthOnly: boolean) => {
        this.eventForm.patchValue({
          startDate: null,
          endDate: null,
        });

        const formats = { ...MY_DATE_FORMATS };
        formats.display.dateInput = isMonthOnly ? 'MM/YYYY' : 'DD/MM/YYYY';
        this.dateAdapter.setLocale('it-IT');
      });
  }

  ngOnInit(): void {
    this.populateFormFields();
  }

  populateFormFields(): void {
    if (this.eventStartDay) {
      this.eventForm.get('isMonthOnly')?.setValue(false);
      const startDateString = `${this.eventStartDay}/${this.eventStartMonth}/${this.eventStartYear}`;
      let endDateString = null;
      if (
        this.eventEndDay !== '' &&
        this.eventEndMonth !== '' &&
        this.eventEndYear !== ''
      ) {
        endDateString = `${this.eventEndDay}/${this.eventEndMonth}/${this.eventEndYear}`;
      }
      this.eventForm.patchValue({
        title: this.eventTitle,
        startDate: moment(startDateString, 'DD/MM/YYYY'),
        endDate: endDateString ? moment(endDateString, 'DD/MM/YYYY') : null,
      });
    } else {
      this.eventForm.get('isMonthOnly')?.setValue(true);
      const startDateString = `${this.eventStartMonth}/${this.eventStartYear}`;
      let endDateString = null;
      if (this.eventEndMonth !== '' && this.eventEndYear !== '') {
        endDateString = `${this.eventEndMonth}/${this.eventEndYear}`;
      }
      this.eventForm.patchValue({
        title: this.eventTitle,
        startDate: moment(startDateString, 'MM/YYYY'),
        endDate: endDateString ? moment(endDateString, 'MM/YYYY') : null,
      });
    }
  }

  onCancel(): void {
    this.cancel.emit();
  }

  handleMonthYearSelection(
    normalizedMonthAndYear: moment.Moment,
    controlName: 'startDate' | 'endDate',
    datepicker: any
  ): void {
    if (this.eventForm.get('isMonthOnly')?.value) {
      const firstDayOfMonth = normalizedMonthAndYear.startOf('month');
      this.eventForm.patchValue({
        [controlName]: firstDayOfMonth,
      });
      datepicker.close();
    }
  }

  onConfirm(): void {
    if (this.isBtnClicked) {
      return;
    }

    const formValues = this.eventForm.value;
    const isMonthOnly = formValues.isMonthOnly;

    const startDateString = formValues.startDate
      ? isMonthOnly
        ? moment(formValues.startDate).format('MM/YYYY')
        : moment(formValues.startDate).format('DD/MM/YYYY')
      : '';

    const endDateString = formValues.endDate
      ? isMonthOnly
        ? moment(formValues.endDate).format('MM/YYYY')
        : moment(formValues.endDate).format('DD/MM/YYYY')
      : '';

    const output: AddEventLifeLine = {
      title: formValues.title,
      start_date: startDateString,
      has_end_date: !!formValues.endDate,
      end_date: endDateString,
    };

    this.isBtnClicked = true;
    this.confirm.emit(output);
  }
}
